import { useNavigate } from "@tanstack/react-router"
import { Badge } from "design-system/atoms/Badge"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { SearchIcon } from "lucide-react"
import { useGetBackofficeBlockedConversationsQuery } from "modules/backoffice/hooks/whatsappRoutes/useGetBackofficeBlockedConversationsQuery"
import { Pagination } from "modules/shared/components/Pagination"
import { SearchQuery } from "modules/shared/components/SearchQuery"
import { usePagination } from "modules/shared/hooks/usePagination"
import { useSearchQuery } from "modules/shared/hooks/useSearchQuery"
import { useTranslation } from "react-i18next"
import { Route as BackofficeWhatsappRoutesRoute } from "routes/_base/_auth/backoffice/whatsappRoutes/route"
import { BackofficeBlockedConversationReasonEnum } from "schema"
import { z } from "zod"

const ITEMS_PER_PAGE = 25
const COLUMNS = 6
const ALL_REASONS = "all"

const ReasonSchema = z.enum(BackofficeBlockedConversationReasonEnum)

export function BlockedConversations() {
  const { t } = useTranslation()
  const search = BackofficeWhatsappRoutesRoute.useSearch()
  const navigate = useNavigate({ from: BackofficeWhatsappRoutesRoute.fullPath })

  const { page, totalPages, setPage, setCount, count } = usePagination({
    search,
    fullPath: BackofficeWhatsappRoutesRoute.fullPath,
    total: ITEMS_PER_PAGE,
  })

  const { query, setQuery } = useSearchQuery({
    search,
    fullPath: BackofficeWhatsappRoutesRoute.fullPath,
    onSearchQueryChange: () => setPage(1),
  })

  const { data, isPending } = useGetBackofficeBlockedConversationsQuery({
    query: { page, total: ITEMS_PER_PAGE, query, reason: search.reason },
    onSuccess: (response) => setCount(response.pageInfo.count),
  })

  function onReasonChange(value: string) {
    const parsed = ReasonSchema.safeParse(value)
    const reason = parsed.success ? parsed.data : undefined

    navigate({ search: (previous) => ({ ...previous, reason, page: 1 }) })
  }

  function renderRows() {
    if (isPending || !data) {
      return <TableSkeleton colSpans={Array(COLUMNS).fill(1)} />
    }

    if (data.length === 0) {
      return (
        <TableEmptySlate
          colSpan={COLUMNS}
          message={t("backoffice.whatsappRoutes.blockedConversations.empty")}
        />
      )
    }

    return data.map((conversation) => (
      <TableRow key={conversation.conversationId}>
        <TableCell>
          <DateDisplay date={conversation.createdAt} />
        </TableCell>
        <TableCell>{conversation.companyName}</TableCell>
        <TableCell className="font-mono">{conversation.conversationId}</TableCell>
        <TableCell>
          {`${conversation.senderNumber ?? "-"} → +${conversation.recipientCallingCode}`}
        </TableCell>
        <TableCell>
          <Badge
            variant={
              conversation.reason === BackofficeBlockedConversationReasonEnum.skipped
                ? "cached"
                : "negative"
            }
          >
            {t(`backoffice.whatsappRoutes.blockedConversations.reasons.${conversation.reason}`)}
          </Badge>
        </TableCell>
        <TableCell>
          {conversation.isRerouted ? t("backoffice.whatsappRoutes.blockedConversations.yes") : "-"}
        </TableCell>
      </TableRow>
    ))
  }

  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-col gap-0.5">
        <h2 className="text-lg font-medium">
          {t("backoffice.whatsappRoutes.blockedConversations.title")}
        </h2>
        <span className="text-sm text-muted-foreground">
          {t("backoffice.whatsappRoutes.blockedConversations.description")}
        </span>
      </div>
      <div className="flex flex-row gap-2">
        <SearchQuery
          placeholder={t("backoffice.whatsappRoutes.blockedConversations.search")}
          setQuery={setQuery}
          defaultValue={query}
          icon={SearchIcon}
        />
        <Select value={search.reason ?? ALL_REASONS} onValueChange={onReasonChange}>
          <SelectTrigger className="w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_REASONS}>
              {t("backoffice.whatsappRoutes.blockedConversations.reasons.all")}
            </SelectItem>
            {Object.values(BackofficeBlockedConversationReasonEnum).map((reason) => (
              <SelectItem key={reason} value={reason}>
                {t(`backoffice.whatsappRoutes.blockedConversations.reasons.${reason}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("backoffice.whatsappRoutes.blockedConversations.table.date")}</TableHead>
            <TableHead>
              {t("backoffice.whatsappRoutes.blockedConversations.table.company")}
            </TableHead>
            <TableHead>
              {t("backoffice.whatsappRoutes.blockedConversations.table.conversation")}
            </TableHead>
            <TableHead>{t("backoffice.whatsappRoutes.blockedConversations.table.route")}</TableHead>
            <TableHead>
              {t("backoffice.whatsappRoutes.blockedConversations.table.reason")}
            </TableHead>
            <TableHead>
              {t("backoffice.whatsappRoutes.blockedConversations.table.rerouted")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>{renderRows()}</TableBody>
      </Table>
      <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} count={count} />
    </section>
  )
}
